import React, { FC } from "react"
import { Link, useNavigate } from "react-router-dom"

import { ActionIcon, Card, Group, Menu, Stack, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconArrowRight, IconDotsVertical } from "@tabler/icons-react"

import DeviceSummaryLocationCard from "../device-summary-location-card/DeviceSummaryLocationCard"
import DeviceSummaryServicesCard from "../device-summary-services-card/DeviceSummaryServicesCard"
import ChangeGroupModal from "../change-group-modal/ChangeGroupModal"
import RemoveFromGroupModal from "../remove-from-group-modal/RemoveFromGroupModal"
import { DeviceDocument } from "../../../../contexts/devices-context"
import { useLanguage } from "../../../../contexts/language-context"
import { useAuth } from "../../../../contexts/authentication-context"
import RequiresRole from "../../../auth/components/require-auth/RequiresRole"

type DeviceSummaryCardProps = {
    device: DeviceDocument
}

const DeviceSummaryCard: FC<DeviceSummaryCardProps> = ({ device }) => {
    const { L } = useLanguage()
    const [changeGroupOpened, { open: openChangeGroup, close: closeChangeGroup }] = useDisclosure(false)
    const [removeFromGroupOpened, { open: openRemoveFromGroup, close: closeRemoveFromGroup }] = useDisclosure(false)
    const navigate = useNavigate()

    return (
        <Card p="0.5rem">
            <Stack spacing="0.5rem" sx={{ cursor: "pointer" }} onClick={() => navigate(`/devices/${device.id}`)}>
                <Group position="apart" noWrap>
                    <Text
                        size="lg"
                        weight={500}
                        p="0.5rem"
                        lh={1}
                        sx={theme => ({ fontFamily: theme.headings.fontFamily })}
                    >
                        {device.name}
                    </Text>
                    <Menu withinPortal>
                        <Menu.Target>
                            <ActionIcon size={38} variant="transparent" onClick={e => e.stopPropagation()}>
                                <IconDotsVertical size={24} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Label>{L("Device")}</Menu.Label>
                            <Menu.Item
                                onClick={e => e.stopPropagation()}
                                rightSection={<IconArrowRight size={14} />}
                                component={Link}
                                to={device.id}
                            >
                                {L("View")}
                            </Menu.Item>
                            <RequiresRole role="edit-devices">
                                    <Menu.Divider />
                                    <Menu.Label>{L("Groups")}</Menu.Label>
                                    <Menu.Item
                                        rightSection={<IconArrowRight size={14} />}
                                        onClick={e => e.stopPropagation()}
                                        component={Link}
                                        to={`/groups/${device.group}`}
                                    >
                                        {L("View group")}
                                    </Menu.Item>
                                    <Menu.Item
                                        onClick={e => {
                                            e.stopPropagation()
                                            openChangeGroup()
                                        }}
                                    >
                                        {device.group === device.organisation ? L("Add to group") : L("Change group")}
                                    </Menu.Item>
                                    <Menu.Item
                                        color="red"
                                        disabled={device.group === device.organisation}
                                        onClick={e => {
                                            e.stopPropagation()
                                            openRemoveFromGroup()
                                        }}
                                    >
                                        {L("Remove from group")}
                                    </Menu.Item>
                            </RequiresRole>
                        </Menu.Dropdown>
                    </Menu>
                </Group>
                {device.health && <DeviceSummaryServicesCard health={device.health} />}
                {device.coordinates && <DeviceSummaryLocationCard coordinates={device.coordinates} />}
                {device.health?.timestamp && (
                    <Text size="xs" ta="center" color="dimmed">
                        {L("Last update at")} {device.health.timestamp.format("D MMM, H:mm")}
                    </Text>
                )}
            </Stack>
            <ChangeGroupModal opened={changeGroupOpened} onClose={closeChangeGroup} device={device} />
            {device.group === device.organisation || (
                <RemoveFromGroupModal opened={removeFromGroupOpened} onClose={closeRemoveFromGroup} device={device} />
            )}
        </Card>
    )
}

export default DeviceSummaryCard
